/**
 * BYO-specific destination step 3: renders FlexFlow's FileSystem for SINK.
 * On "Save": creates the sink via addDataSink + updateDataSetInfo.
 */

import React, { useCallback } from 'react';
import { FORM_TYPE_CREATE_DIRECT_FLOW } from 'js/constants/formTypes';
import {
  addDataSink,
  updateDataSetInfo,
  loadDataSource,
  activateSink,
  updateSinkInfo,
} from 'js/actions';
import { activateSource, pauseSource } from 'js/actions/sources';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { activateByoSourceAndResolveNexset } from 'new/components/flows/types/byo/core/services';
import { FileSystem } from 'new/components/flows/configure/FileSystem';
import { FileUtil } from 'new/utils/FileUtil';
import { useDirectFlow } from 'new/components/flows/types/DirectFlow/hooks/useDirectFlow';
import useConfig from 'new/components/flows/types/DirectFlow/hooks/useConfig';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import { useForm } from 'new/elements/Form/useForm';
import useFlowParams from 'new/components/flows/useFlowParams';
import type { FCFormPageType } from 'new/elements/Form/useForm';
import { useStudioDraftCtx } from '../studio/StudioDraftContext';
import {
  ByoConfigureStepFrame,
  createOnceOrUpdate,
  credentialPatch,
  useByoConfigureStepSave,
} from './ByoConfigureStepFrame';

const FORM = FORM_TYPE_CREATE_DIRECT_FLOW;

export const ByoDestinationConfigureStep: FCFormPageType<{}> = () => {
  const dispatch = useAppDispatch();
  const { nextPage } = useForm();
  const { sinkConfig, onSinkConfigChanged } = useConfig();
  const { onBucketSelected } = useFlowsSetup(FORM, 'custom');
  const { selectedConnection, credentialId, selectedBucket, sink } = useDirectFlow();
  const { onDismiss, nexsetId: ctxNexsetId, sourceId: ctxSourceId, id: editId } = useFlowParams();
  // In edit mode FlowParamsProvider passes the sink id; create mode leaves it unset.
  const editSinkId = editId != null && !Number.isNaN(Number(editId)) ? Number(editId) : null;
  // Edit mode is hydrated once useStartSink has loaded this sink and the
  // folder it saved. sinkConfig alone isn't enough: right after a create it
  // lands before the sink does, and mounting FileSystem then makes
  // SelectBucket probe the bucket root and auto-select it, overwriting the
  // saved folder on the next Save.
  const editHydrated =
    editSinkId == null ||
    (sink?.id === editSinkId &&
      !!sinkConfig &&
      (!!(selectedBucket as any)?.path || !(sinkConfig as any).path));
  // What this create attempt has already persisted (see createOnceOrUpdate).
  const { sinkCreateProgressRef } = useStudioDraftCtx();

  const saveDestination = useCallback(async () => {
    // selectedBucket is a Bucket object {id,path,...}; prefer the string from sinkConfig.
    const path = (sinkConfig as any)?.path ?? (selectedBucket as any)?.path ?? '';
    const sink_config: Record<string, any> = {
      'ui.flow_type': 'custom',
      mapping: { mode: 'auto', tracker_mode: 'NONE' },
      path,
      'ui.display_path': path,
      'output.dir.name.pattern': '{yyyy}/{MM}/{dd}',
      ...(sinkConfig ?? {}),
    };

    // Edit mode: update the existing sink in place — do NOT create a new one.
    if (editSinkId != null) {
      // useStartSink hydrates sinkConfig asynchronously (two API round-trips).
      // startPage=2 opens this step immediately, so a Save click before
      // hydration lands would build sink_config with an empty path and
      // updateSinkInfo does a full replace (not a merge) — wiping the sink's
      // real config. Bail until hydration has finished.
      if (!editHydrated) return;
      if (!path) throw new Error('Select a destination folder before saving.');
      // Mirrors DirectFlow SinkConfigureStep's update: keep the sink's name
      // unless the user changed it (FileSystem normalizes a name edit into
      // sinkConfig.name) — this used to overwrite it with the last folder
      // segment on every save — and send the (possibly changed) credential,
      // which was previously dropped. NEX-22636.
      await dispatch(
        updateSinkInfo(editSinkId, {
          name:
            (sinkConfig as any)?.name ||
            (sink as any)?.name ||
            path.split('/').pop() ||
            'BYO destination',
          sink_config,
          ...credentialPatch(credentialId),
        }) as any,
      );
      onDismiss?.({ addedDataSinkId: editSinkId });
      return true;
    }

    let dataSetId: any = ctxNexsetId ?? null;
    let activatedForNexsetPoll = false;

    // Fallback only: a nexset that didn't arrive via context (e.g. an older
    // source from before ByoSourceConfigureStep created one explicitly)
    // gets one resolved here — which requires activating the source first.
    if (dataSetId == null && ctxSourceId != null) {
      activatedForNexsetPoll = true;
      dataSetId = await activateByoSourceAndResolveNexset(ctxSourceId, {
        dispatch: dispatch as any,
        activateSourceAction: activateSource,
        loadDataSourceAction: loadDataSource as any,
      });
    }

    if (sinkCreateProgressRef.current.sourceId !== (ctxSourceId ?? null)) {
      sinkCreateProgressRef.current = {
        sourceId: ctxSourceId ?? null,
        sinkId: null,
        linked: false,
        inputsKey: null,
      };
    }
    const progress = sinkCreateProgressRef.current;
    const credential = credentialPatch(credentialId);
    const sinkName = path.split('/').pop() || 'BYO destination';
    let newId: number | string;
    try {
      // No nexset → no sink can be attached. This used to call
      // onDismiss({}) and return true, which ByoDestShellNode read as
      // success and navigated away with no destination ever created.
      // NEX-22636.
      if (dataSetId == null) {
        throw new Error(
          "The flow's dataset could not be found, so the destination was not created. Please try again.",
        );
      }

      // On a changed retry (after the link failed) the sink is updated with
      // DirectFlow's sink-update PUT shape, never attached with a stale config.
      newId = await createOnceOrUpdate(
        progress,
        'sinkId',
        [selectedConnection, credential.data_credentials_id, sink_config],
        {
          create: async () => {
            const res: any = await dispatch(
              addDataSink(
                selectedConnection,
                {
                  name: sinkName,
                  description: '',
                  flow_type: 'custom',
                  data_set_id: dataSetId,
                  sink_config,
                },
                credentialId,
              ) as any,
            );
            return res?.result ?? res?.response?.id ?? null;
          },
          update: (sinkId) =>
            dispatch(
              updateSinkInfo(sinkId, {
                name: sinkName,
                sink_type: selectedConnection,
                sink_config,
                ...credential,
              }) as any,
            ),
          createFailedMessage: 'The BYO destination could not be created.',
        },
      );

      if (!progress.linked) {
        await dispatch(updateDataSetInfo(dataSetId, { data_sinks: [newId] }) as any);
        progress.linked = true;
      }
    } catch (e) {
      // Don't leave a source running with no linked destination behind a failed step.
      if (activatedForNexsetPoll && ctxSourceId != null) {
        try {
          await dispatch(pauseSource(ctxSourceId) as any);
        } catch {
          /* best-effort rollback */
        }
      }
      throw e;
    }

    // Sinks are created paused; activate so the flow actually writes files
    // on run. Mirrors useStudioDraft's create path. Non-fatal on failure.
    try {
      await dispatch(activateSink(newId) as any);
    } catch {
      /* non-fatal */
    }

    // The source is never activated at creation time (Step 1) — BYO is a
    // one-shot job, and activating a source with no code/destination
    // triggered the generic ingestion-scan machinery for no reason
    // (confirmed 2026-08-20). It must still be activated here even when
    // `dataSetId` came from context (regression 2026-08-23). It now runs
    // only AFTER the sink exists and is linked: activating first (the old
    // order) left an ACTIVE source with no destination whenever the sink
    // create/link failed. DirectFlow's SinkConfigureStep creates the sink
    // without any source activation at all, so activation isn't a
    // precondition for sink creation. NEX-22636.
    if (ctxSourceId != null && !activatedForNexsetPoll) {
      try {
        await dispatch(activateSource(ctxSourceId) as any);
      } catch {
        /* non-fatal — matches activateByoSourceAndResolveNexset's own tolerance */
      }
    }

    onDismiss?.({ addedDataSinkId: newId });
    return true;
  }, [
    onDismiss,
    editSinkId,
    editHydrated,
    ctxNexsetId,
    ctxSourceId,
    sinkConfig,
    selectedConnection,
    credentialId,
    selectedBucket,
    sink,
    dispatch,
    sinkCreateProgressRef,
  ]);

  const saveError = useByoConfigureStepSave(saveDestination, 'Failed to save the BYO destination.');

  return (
    <ByoConfigureStepFrame hydrating={!editHydrated} saveError={saveError}>
      <FileSystem
        formType={FORM}
        shouldShowLoading
        hideMetaConfiguration
        flowType="custom"
        connectionType="SINK"
        sink={sink}
        selectedBucket={selectedBucket}
        selectedConnection={selectedConnection}
        credentialId={credentialId}
        sinkConfig={sinkConfig}
        // Shows the date-prefix box next to the folder ({yyyy}/{MM}/{dd} unless
        // the sink saved its own pattern), same check as DirectFlow's sink step.
        allowCustomPrefixEditor={
          !!FileUtil.isAllowedToUseCustomPrefixEditor(selectedBucket, sinkConfig)
        }
        onBucketSelected={onBucketSelected}
        onSinkConfigChanged={onSinkConfigChanged}
        onSaveAdvancedMode={onSinkConfigChanged}
        nextFormStage={nextPage}
      />
    </ByoConfigureStepFrame>
  );
};

ByoDestinationConfigureStep.formConfig = { title: 'Configure' };

export default ByoDestinationConfigureStep;
